import { useEffect, useState } from 'react';

import LockOutlineIcon from '@/assets/svg/ui-elements/lock-outline.svg';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { Button, Modal, theme } from '@/shared/design-system';

import {
  ActionInfoCard,
  ActionModalActions,
  ActionModalBody,
  ActionModalSeparator,
} from './action-modal-shared';
import {
  ActionReasonFields,
  ActionReasonValues,
  areActionReasonValuesValid,
  emptyActionReasonValues,
} from './action-reason-fields';

export type BlockActionModalProps = {
  type: 'fund' | 'entity' | 'user';
  onConfirm: (reasons: ActionReasonValues) => void | Promise<void>;
  isLoading?: boolean;
};

const subjectByType: Record<BlockActionModalProps['type'], string> = {
  fund: 'Fund',
  entity: 'Entity',
  user: 'User',
};

export const BlockActionModal = () => {
  const { open, handleClose, props } = useGlobalModal('BLOCK_ACTION_MODAL');
  const [isPending, setIsPending] = useState(false);
  const [reasons, setReasons] = useState<ActionReasonValues>(emptyActionReasonValues);

  const subject = subjectByType[props?.type ?? 'user'];

  useEffect(() => {
    if (!open) setReasons(emptyActionReasonValues);
  }, [open]);

  const handleConfirm = async () => {
    setIsPending(true);
    try {
      await props?.onConfirm({
        reason: reasons.reason.trim(),
        internalReference: reasons.internalReference.trim(),
      });
    } finally {
      setIsPending(false);
    }
  };

  const isLoading = isPending || props?.isLoading;
  const canConfirm = !isLoading && areActionReasonValuesValid(reasons);

  return (
    <Modal
      open={open}
      onOpenChange={(o) => (!o ? handleClose() : undefined)}
      title={`Block ${subject}`}
      showClose
    >
      <ActionModalBody>
        <ActionModalSeparator />
        <ActionInfoCard
          icon={<LockOutlineIcon width={24} height={24} color={theme.colors.icon.error} />}
          title={`Are you sure you want to Block this ${subject}?`}
        />
        <ActionReasonFields
          sharedWithLabel={`Shared with the ${subject}`}
          values={reasons}
          onChange={setReasons}
          disabled={isLoading}
        />
        <ActionModalSeparator />
        <ActionModalActions>
          <Button
            color="destructive"
            variant="solid"
            size="lg"
            fullWidth
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            Block
          </Button>
          <Button color="tertiary" variant="outline" size="lg" fullWidth onClick={handleClose}>
            Cancel
          </Button>
        </ActionModalActions>
      </ActionModalBody>
    </Modal>
  );
};
